/* Authenticated Centrifugo client shared by all mounted Svelte screens. */

import { Centrifuge, type Subscription } from "centrifuge";
import { api } from "./api";
import { get } from "svelte/store";
import { auth, refreshMe } from "./auth";

let client: Centrifuge | null = null;
const channelEntries = new Map<
  string,
  { subscription: Subscription; handlers: Set<(event: RealtimeEvent) => void> }
>();
const seenEventIds = new Set<string>();
const MAX_SEEN_EVENTS = 512;

export type RealtimeEvent = {
  id: string;
  type: string;
  tenant_id: number;
  occurred_at: string;
  actor_user_id: number | null;
  data: Record<string, unknown>;
};

export type RealtimeListener = { unsubscribe: () => void };

export function realtimeWebSocketUrl(): string {
  const configured = (import.meta as any).env?.VITE_CENTRIFUGO_WS as string | undefined;
  if (configured) return configured;
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}/centrifugo/connection/websocket`;
}

function toEvent(value: unknown): RealtimeEvent | null {
  if (!value || typeof value !== "object") return null;
  const event = value as Partial<RealtimeEvent>;
  if (typeof event.id !== "string" || typeof event.type !== "string") return null;
  return {
    id: event.id,
    type: event.type,
    tenant_id: typeof event.tenant_id === "number" ? event.tenant_id : 0,
    occurred_at: typeof event.occurred_at === "string" ? event.occurred_at : "",
    actor_user_id: typeof event.actor_user_id === "number" ? event.actor_user_id : null,
    data: event.data && typeof event.data === "object" ? event.data : {},
  };
}

function rememberEvent(id: string): boolean {
  if (seenEventIds.has(id)) return false;
  seenEventIds.add(id);
  if (seenEventIds.size > MAX_SEEN_EVENTS) {
    const oldest = seenEventIds.values().next().value;
    if (oldest) seenEventIds.delete(oldest);
  }
  return true;
}

export function getRealtime(): Centrifuge {
  if (client) return client;
  client = new Centrifuge(realtimeWebSocketUrl(), {
    getToken: async () => {
      const r = await api.post<{ token: string }>("/realtime/token", { scope: "user" });
      return r.token;
    },
  });
  client.connect();
  return client;
}

export async function subscribe(
  channel: string,
  handler: (event: RealtimeEvent) => void,
): Promise<RealtimeListener> {
  const match = /^tenant:(\d+):order:/.exec(channel);
  if (match) {
    const member = get(auth).memberships.find(m=>m.tenant.id === Number(match[1]));
    if (member?.permission_version === 2) channel = channel.replace(':order:', `:r${member.permission_revision}:order:`);
  }
  const existing = channelEntries.get(channel);
  if (existing) {
    existing.handlers.add(handler);
    return listenerFor(channel, handler);
  }

  const c = getRealtime();
  const sub = c.newSubscription(channel, {
    getToken: async () => {
      const r = await api.post<{ token: string }>("/realtime/token", {
        scope: "channel",
        channel,
      });
      return r.token;
    },
  });
  const entry = { subscription: sub, handlers: new Set([handler]) };
  channelEntries.set(channel, entry);
  sub.on("publication", (ctx) => {
    const event = toEvent(ctx.data);
    if (!event || !rememberEvent(event.id)) return;
    if (event.type === "permissions.changed") void refreshMe();
    for (const callback of entry.handlers) callback(event);
  });
  sub.subscribe();
  return listenerFor(channel, handler);
}

function listenerFor(
  channel: string,
  handler: (event: RealtimeEvent) => void,
): RealtimeListener {
  let active = true;
  return {
    unsubscribe: () => {
      if (!active) return;
      active = false;
      const entry = channelEntries.get(channel);
      if (!entry) return;
      entry.handlers.delete(handler);
      if (entry.handlers.size === 0) {
        entry.subscription.unsubscribe();
        client?.removeSubscription(entry.subscription);
        channelEntries.delete(channel);
      }
    },
  };
}

export function disconnectRealtime(): void {
  for (const entry of channelEntries.values()) entry.subscription.unsubscribe();
  channelEntries.clear();
  seenEventIds.clear();
  client?.disconnect();
  client = null;
}
